import { Dashboard } from '../../models/Dashboard'
import { Deposit } from '../../models/Deposit'
import { RecordFactory } from '../../models/RecordFactory'
import { currencyFormat } from '../../utils/numbers'
import { ComponentData } from '../../utils/typing'
import { Icon } from '../Icon'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import {
    ClickableTableRow,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../app/design/Table'
import { DashboardSkeleton } from '../common/skeletons/DashboardSkeleton'
import { EmptyDashboardLayout } from './EmptyDashboardLayout'
import { KeyMetric } from './KeyMetric'

type Props = {
    deposits: Deposit[]
    dashboard?: Dashboard
    searchText: string
    isLoading: boolean
}

const viewWillAppear = async (base: PocketComponent<Props>) => {
    return await Promise.all(
        base.props.deposits.map((d) => RecordFactory.getInstance().loadDeposit(d.id!))
    )
}

const render = ({
    base: {
        props: { deposits, dashboard, searchText, isLoading },
    },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>, Props>) => {
    if (isLoading) {
        return <DashboardSkeleton />
    }

    return (
        <div className="flex flex-col gap-6">
            <KeyMetric
                metrics={[
                    {
                        title: deposits.length === 1 ? `Deposit` : `Deposits`,
                        value: (dashboard?.count ?? 0).toString(),
                    },
                    {
                        title: 'Amount deposited',
                        value: currencyFormat(dashboard?.value ?? 0),
                    },
                ]}
            />
            <Table>
                <TableHeader>
                    <TableRow location="head">
                        <TableHead className="font-normal">Account/card</TableHead>
                        <TableHead className="font-normal">Payment date</TableHead>
                        <TableHead className="text-right font-normal">Amount</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {deposits.map((d) => (
                        <ClickableTableRow key={d.id} href={`/deposits/${d.id}`}>
                            <TableCell>
                                <div className="flex items-center gap-4">
                                    {['checking', 'savings'].includes(d.source?.type ?? '') ? (
                                        <>
                                            <Icon
                                                background="round"
                                                icon="bankCard.small"
                                                className="size-9"
                                            />
                                            <Label size="sm">{d.source?.toString()}</Label>
                                        </>
                                    ) : (
                                        <>
                                            <Icon
                                                background="round"
                                                icon="bank.small"
                                                className="size-9"
                                            />
                                            <Label size="sm">{d.source?.name}</Label>
                                        </>
                                    )}
                                </div>
                            </TableCell>
                            <TableCell>{d.createdDate?.format('MMM D, YYYY')}</TableCell>
                            <TableCell>
                                <Paragraph size="sm" className="text-right">
                                    {currencyFormat(d.amount)}
                                </Paragraph>
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {deposits.length === 0 && (
                        <TableCell colSpan={10}>
                            <EmptyDashboardLayout dashboard={dashboard} searchtext={searchText} />
                        </TableCell>
                    )}
                </TableBody>
            </Table>
        </div>
    )
}

export const DepositsDashboard = usePSComponent(render, viewWillAppear)
